import React, { memo, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import useSimulationStore from '../../../store/simulationStore';

interface EnergyDistributionChartProps {
    height?: number;
}

const EnergyDistributionChart: React.FC<EnergyDistributionChartProps> = memo(({ height = 250 }) => {
    // Only subscribe to the specific values needed
    const totalEnergy = useSimulationStore(state => state.totalEnergy);
    const translationalEnergy = useSimulationStore(state => state.translationalEnergy);
    const rotationalEnergy = useSimulationStore(state => state.rotationalEnergy);
    const initialTotalEnergy = useSimulationStore(state => state.initialTotalEnergy);
    const energyRatio = useSimulationStore(state => state.energyRatio);

    // Memoize chart data
    const chartData = useMemo(() => [
        {
            name: 'Current',
            translational: translationalEnergy,
            rotational: rotationalEnergy,
        },
        {
            name: 'Initial',
            translational: initialTotalEnergy * 0.7,
            rotational: initialTotalEnergy * 0.3,
        },
    ], [translationalEnergy, rotationalEnergy, initialTotalEnergy]);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Summary */}
            <div style={summaryStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>Total: <strong style={{ color: '#4fc3f7' }}>{totalEnergy.toFixed(2)}</strong></span>
                    <span>Ratio: <strong style={{ color: energyRatio > 1.1 ? '#f48fb1' : '#81c784' }}>{(energyRatio * 100).toFixed(1)}%</strong></span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right' }}>
                    <span>Trans: <strong style={{ color: '#4fc3f7' }}>{translationalEnergy.toFixed(2)}</strong></span>
                    <span>Rot: <strong style={{ color: '#81c784' }}>{rotationalEnergy.toFixed(2)}</strong></span>
                </div>
            </div>

            {/* Chart */}
            <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis
                            dataKey="name"
                            stroke="#888"
                            tick={{ fill: '#ccc', fontSize: 11 }}
                        />
                        <YAxis
                            stroke="#888"
                            tick={{ fill: '#ccc', fontSize: 10 }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'rgba(30, 30, 30, 0.95)',
                                border: '1px solid #4fc3f7',
                                borderRadius: '4px',
                                color: '#fff'
                            }}
                            formatter={(value: number) => value.toFixed(2)}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Bar
                            dataKey="translational"
                            stackId="energy"
                            fill="#4fc3f7"
                            name="Translational"
                            isAnimationActive={false}
                        />
                        <Bar
                            dataKey="rotational"
                            stackId="energy"
                            fill="#81c784"
                            name="Rotational"
                            radius={[4, 4, 0, 0]}
                            isAnimationActive={false}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
});

EnergyDistributionChart.displayName = 'EnergyDistributionChart';

const summaryStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(40, 40, 40, 0.5)',
    borderRadius: '4px',
    marginBottom: '8px',
    fontSize: '10px',
    color: '#ccc',
    fontFamily: 'monospace',
};

export default EnergyDistributionChart;
